import {useCallback} from 'react';

import {useTranslation} from 'react-i18next';

import {AuthErrorNames} from '@/services/amplify/AmplifyConstants';

// Raw error message returned by Amplify/Cognito when the email fails
// server-side format validation — not something we control, so it can't
// live in i18n like the rest of the copy.
const AMPLIFY_INVALID_EMAIL_ERROR_MESSAGE = 'Invalid email address format.';

interface SignUpErrorToast {
  summary: string;
  message: string;
}

/**
 * Maps a sign-up API error to the {summary, message} pair shown in the
 * snackbar. A match on the raw error message (e.g. invalid email format)
 * takes precedence over a match on the error name; falls back to a generic
 * failure toast otherwise.
 */
export function useSignUpErrorToast() {
  const {t} = useTranslation();

  return useCallback(
    (errorName: string, errorMessage: string): SignUpErrorToast => {
      const errorNameToast: Record<string, SignUpErrorToast> = {
        [AuthErrorNames.UserLambdaValidationException]: {
          summary: t('sign_up.errors.user_already_exists.summary'),
          message: t('sign_up.errors.user_already_exists.message'),
        },
        [AuthErrorNames.InvalidParameterException]: {
          summary: t('sign_up.errors.invalid_parameters.summary'),
          message: t('sign_up.errors.invalid_parameters.message'),
        },
      };

      const errorMessageToast: Record<string, SignUpErrorToast> = {
        [AMPLIFY_INVALID_EMAIL_ERROR_MESSAGE]: {
          summary: t('sign_up.errors.invalid_email.summary'),
          message: t('sign_up.errors.invalid_email.message'),
        },
      };

      const defaultToast: SignUpErrorToast = {
        summary: t('sign_up.errors.failed.summary'),
        message: t('sign_up.errors.failed.message'),
      };

      return (
        errorMessageToast[errorMessage] ||
        errorNameToast[errorName] ||
        defaultToast
      );
    },
    [t],
  );
}
